import { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  LogOut,
  Home,
  Settings,
  Users,
  Image,
  ChevronDown,
  BookA,
  Video,
  Warehouse,
  Store,
  Ticket,
  ListOrdered
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ROLES } from "@/constants/roles";
import { MenuItem } from "@/types/auth.types";

const MainLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  // 检查用户是否有权限访问菜单项
  const hasMenuAccess = (roles?: string[]) => {
    // 如果没有指定角色要求，所有人可访问
    if (!roles || roles.length === 0) return true;
    // 检查用户是否具有所需角色
    return roles.some((role) =>
      user?.roles?.some((userRole) => userRole.code === role)
    );
  };

  // 菜单配置
  const menuItems: MenuItem[] = [
    {
      title: "控制台",
      path: "/dashboard",
      icon: <Home className="h-5 w-5" />,
      // 所有人可访问
    },
    {
      title: "会员管理",
      path: "/members",
      icon: <Users className="h-5 w-5" />,
      roles: [ROLES.ADMIN],
      children: [
        {
          title: "会员列表",
          path: "/members/index",
          roles: [ROLES.ADMIN],
        },
      ],
    },
    {
      title: "邀请管理",
      path: "/invite",
      icon: <Image className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "邀请列表",
          path: "/invite/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "课程管理",
      path: "/courses",
      icon: <Video className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.OPERATOR],
      children: [
        {
          title: "课程列表",
          path: "/courses/index",
          roles: [ROLES.ADMIN, ROLES.OPERATOR],
        },
      ],
    },
    {
      title: "单词管理",
      path: "/words",
      icon: <BookA className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "单词列表",
          path: "/words/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "商品管理",
      path: "/commodities",
      icon: <Warehouse className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "商品列表",
          path: "/commodities/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "活动管理",
      path: "/activities",
      icon: <Store className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "活动列表",
          path: "/activities/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "兑换码管理",
      path: "/codes",
      icon: <Ticket className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "兑换码列表",
          path: "/codes/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "订单管理",
      path: "/orders",
      icon: <ListOrdered className="h-5 w-5" />,
      roles: [ROLES.ADMIN, ROLES.EDITOR],
      children: [
        {
          title: "订单列表",
          path: "/orders/index",
          roles: [ROLES.ADMIN, ROLES.EDITOR],
        },
      ],
    },
    {
      title: "系统设置",
      path: "/settings",
      icon: <Settings className="h-5 w-5" />,
      roles: [ROLES.ADMIN],
    },
  ];

  // 过滤有权限访问的菜单项
  const filterMenuItems = (items: MenuItem[]): MenuItem[] => {
    return items.filter((item) => {
      // 检查主菜单权限
      if (!hasMenuAccess(item.roles)) return false;

      // 如果有子菜单，过滤子菜单
      if (item.children) {
        const filteredChildren = item.children.filter((child) =>
          hasMenuAccess(child.roles)
        );

        // 如果过滤后没有子菜单，且父菜单本身没有页面，则不显示
        if (filteredChildren.length === 0) return false;

        return {
          ...item,
          children: filteredChildren,
        };
      }
      return true;
    });
  };

  // 获取经过权限过滤的菜单项
  const authorizedMenuItems = filterMenuItems(menuItems);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleMenu = (path: string) => {
    setExpandedMenus((prev) =>
      prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]
    );
  };

  const isActive = (path: string) => {
    // For exact matches (dashboard and settings)
    if (path === "/dashboard" || path === "/settings") {
      return location.pathname === path;
    }

    // For parent menu items
    if (
      path === "/members" ||
      path === "/invite" ||
      path === "/courses" ||
      path === "/words"
    ) {
      // Parent should be active if current path starts with parent path
      return location.pathname.startsWith(path);
    }

    // For child routes (including index pages)
    return location.pathname === path;
  };

  const isMenuExpanded = (path: string) => expandedMenus.includes(path);

  return (
    <div className="fixed flex inset-0 h-screen bg-gray-50">
      {/* 侧边栏 */}
      <aside
        className={`
          fixed md:relative inset-y-0 left-0 w-64 bg-white shadow-lg
          transform transition-all duration-300 ease-in-out z-30
          ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0
        `}
      >
        {/* Logo区域 */}
        <div className="flex items-center h-16 px-6 border-b border-gray-200">
          <img
            src="/images/Group 1000004085.png"
            className="h-8 w-auto object-contain"
            alt="溜溜英语"
          />
        </div>

        {/* 导航菜单 */}
        <nav className="px-4 py-4 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {authorizedMenuItems.map((item) => (
            <div key={item.path} className="space-y-1">
              <button
                onClick={() =>
                  item.children ? toggleMenu(item.path) : navigate(item.path)
                }
                className={`
                  w-full flex items-center justify-between px-4 py-2.5 text-sm
                  rounded-lg transition-colors duration-200 text-gray-700 hover:bg-gray-100`}
              >
                <div
                  className={`flex items-center space-x-3 ${
                    isActive(item.path) ? "text-indigo-600" : "text-gray-700"
                  }`}
                >
                  {item.icon}
                  <span>{item.title}</span>
                </div>
                {item.children && (
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200
                      ${isMenuExpanded(item.path) ? "rotate-180" : ""}
                    `}
                  />
                )}
              </button>

              {item.children && isMenuExpanded(item.path) && (
                <div className="pl-10 space-y-1">
                  {item.children.map((child) => (
                    <Link
                      key={child.path}
                      to={child.path}
                      className={`
                        block px-4 py-2 text-sm rounded-lg transition-colors duration-200
                        ${
                          isActive(child.path)
                            ? "bg-indigo-50 text-indigo-600"
                            : "text-gray-600 hover:bg-gray-100"
                        }
                      `}
                    >
                      {child.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </aside>

      {/* 主内容区 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部导航栏 */}
        <header className="bg-white shadow-sm z-20">
          <div className="flex items-center justify-between px-6 h-16">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              {sidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            <div className="flex items-center space-x-4">
              {user && (
                <span className="text-sm text-gray-600">
                  欢迎, {user.real_name}
                </span>
              )}

              {/* 登出确认对话框 */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2 px-4 py-2 text-sm
                    text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>退出登录</span>
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-white">
                  <AlertDialogHeader>
                    <AlertDialogTitle>确认退出</AlertDialogTitle>
                    <AlertDialogDescription>
                      您确定要退出登录吗？
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>取消</AlertDialogCancel>
                    <AlertDialogAction onClick={handleLogout}>
                      确认退出
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </header>

        {/* 页面内容 */}
        <main className="flex-1 bg-gray-50 p-6 relative">
          <div className="absolute inset-0">
            <Outlet />
          </div>
        </main>
      </div>

      {/* 移动端侧边栏遮罩 */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 md:hidden z-20"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default MainLayout;
